import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
/*
 * This file is part of huoxin/filter-rule-manager.
 *
 * Copyright (c) 2026 huoxin.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import FilterRuleModal from './components/FilterRuleModal';
/**
 * Watches the FilterEngine and dispatches non-inline display modes:
 *
 *   - `toast`  → app.alerts.show / app.alerts.dismiss; re-shown if the user
 *                manually dismisses while the rule is still firing
 *   - `modal`  → app.modal.show (once per ruleset firing — re-opens only
 *                after the rule clears and triggers again)
 *
 * `banner` and `sidebar` are rendered inline by FilterRuleInlineDisplay and
 * don't pass through this dispatcher.
 */
export default class FilterRulePopupDispatcher {
  constructor(engine) {
    _defineProperty(this, "engine", void 0);
    _defineProperty(this, "_displayed", void 0);
    _defineProperty(this, "_unsubscribe", void 0);
    this.engine = engine;
    this._displayed = new Map();
    this._unsubscribe = engine.subscribe(() => this.dispatch());
  }
  dispose() {
    if (typeof this._unsubscribe === 'function') this._unsubscribe();
    this.dismissAll();
  }
  dispatch() {
    const application = app;
    if (!application || !application.alerts || !application.modal) return;
    const seen = new Set();
    for (const alert of this.engine.activeAlerts) {
      const id = "rs-".concat(alert.ruleset.id);
      const displayMode = alert.ruleset.displayMode;
      const type = alert.ruleset.interventionType === 'warning' ? 'warning' : 'info';
      const settings = alert.displaySettings || {};
      seen.add(id);
      this._maybeShow(id, displayMode, type, alert.message, settings);
    }
    this.engine.blockResults.forEach((alert, i) => {
      const id = "block-".concat(i, "-").concat(alert.message);
      const settings = alert.displaySettings || {};
      seen.add(id);
      this._maybeShow(id, alert.displayMode, 'block', alert.message, settings);
    });
    for (const id of Array.from(this._displayed.keys())) {
      if (!seen.has(id)) this._dismiss(id);
    }
  }
  _maybeShow(id, displayMode, type, message, displaySettings) {
    if (displaySettings === void 0) {
      displaySettings = {};
    }
    if (displayMode !== 'toast' && displayMode !== 'modal') return;
    const application = app;
    const existing = this._displayed.get(id);
    if (existing) {
      return;
    }
    if (displayMode === 'toast') {
      const defaultToastType = type === 'block' ? 'error' : type;
      const alertAttrs = {
        type: displaySettings.toastTheme || defaultToastType,
        dismissible: true
      };
      if (displaySettings.icon && displaySettings.icon !== 'none') {
        alertAttrs.icon = displaySettings.icon;
      }
      if (displaySettings.title) {
        const trans = application.translator.trans(displaySettings.title);
        alertAttrs.title = m.trust(Array.isArray(trans) ? trans.join('') : String(trans));
      }
      const alertKey = application.alerts.show(alertAttrs, m.trust(message));
      this._displayed.set(id, {
        displayMode,
        alertKey
      });
      return;
    }
    if (displayMode === 'modal') {
      application.modal.show(FilterRuleModal, {
        message,
        type,
        displaySettings
      });
      this._displayed.set(id, {
        displayMode,
        alertKey: null
      });
      return;
    }
  }
  _dismiss(id) {
    const info = this._displayed.get(id);
    if (!info) return;
    const application = app;
    if (info.displayMode === 'toast' && info.alertKey != null && application && application.alerts) {
      try {
        application.alerts.dismiss(info.alertKey);
      } catch (e) {
        /* ignore */
      }
    }
    this._displayed.delete(id);
  }
  dismissAll() {
    for (const id of Array.from(this._displayed.keys())) this._dismiss(id);
  }
}